/**
 * 业务辅助格式化工具
 *
 * 包含特定业务场景逻辑（如中文面议、Java 后端数据格式）。
 */

/**
 * 格式化价格显示（支持面议状态）
 *
 * @category Business Helpers
 * @param price - 价格值
 * @param options - 配置选项
 * @returns 格式化后的价格字符串
 */
export function formatPrice(
  price: number | string | undefined | null,
  options?: { prefix?: string; suffix?: string; negotiableText?: string },
): string {
  const { prefix = '', suffix = '', negotiableText = '-' } = options || {};

  if (price === undefined || price === null || price === '')
    return negotiableText;

  const priceNum = Number(price);
  if (Number.isNaN(priceNum) || priceNum < 0) {
    return negotiableText;
  }

  return `${prefix}${priceNum}${suffix}`;
}

/**
 * 判断价格是否为面议
 *
 * 统一的价格面议判断逻辑，支持 undefined / null / 空字符串 / NaN / 负数。
 *
 * @category Business Helpers
 * @param price - 价格值
 * @returns 是否面议
 */
export function isNegotiablePrice(
  price: number | string | undefined | null,
): boolean {
  if (price === undefined || price === null || price === '') return true;
  const priceNum = Number(price);
  return Number.isNaN(priceNum) || priceNum < 0;
}

/**
 * 格式化地址显示
 *
 * 将逗号分隔的地址字符串替换为指定分隔符（默认空格）。
 *
 * @category Business Helpers
 * @param location - 地址字符串
 * @param separator - 分隔符，默认 ' '
 * @returns 格式化后的地址
 */
export function formatLocation(
  location: string | undefined | null,
  separator: string = ' ',
): string {
  if (!location) return '';
  return location.replace(/,/g, separator).trim();
}

/** 布尔标志类型：支持数字、字符串、布尔值 */
type BooleanFlag = number | string | boolean | undefined | null;

/**
 * 检查布尔标志是否为真值
 *
 * 支持后端返回的多种布尔表示形式（数字 1、字符串 "1"、布尔值等）。
 *
 * @category Business Helpers
 * @param flag - 布尔标志值
 * @param trueLabels - 额外的真值标签列表
 * @returns 是否为真
 */
export function checkBooleanFlag(
  flag: BooleanFlag,
  trueLabels: string[] = [],
): boolean {
  if (flag === undefined || flag === null) return false;
  const strValue = String(flag);
  return (
    flag === 1 ||
    flag === true ||
    ['1', 'true', 'yes', ...trueLabels].includes(strValue.toLowerCase())
  );
}

/** Java 字符串数组输入类型：字符串或字符串数组 */
type JavaStringArrayInput = string | string[] | undefined | null;

/**
 * 解析 Java String 数组格式
 *
 * 支持 JSON 数组字符串 `["a","b"]` 和普通字符串，自动过滤空值。
 * 用于对接 Java 后端返回的字符串数组字段。
 *
 * @category Business Helpers
 * @param val - 输入值
 * @returns 解析后的字符串数组
 */
export function parseJavaStringArray(val: JavaStringArrayInput): string[] {
  if (!val) return [];

  if (Array.isArray(val)) {
    return val.filter((v) => v && typeof v === 'string' && v.trim() !== '');
  }

  const trimmed = val.trim();
  if (!trimmed) return [];

  // 优先尝试 JSON.parse
  if (trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => String(item).trim())
          .filter((item) => item !== '');
      }
    } catch {
      // JSON 解析失败，当作单值
    }
  }

  return [trimmed];
}
